import { compareImages, decodedPixelHash, exactHash, hammingDistance } from "./metrics";
import { assessCameraOriginal, inspectContainer, readFullMetadata } from "./forensics";
import type { DuplicateGroup, OriginalEvidence, PhotoRecord, ScanProgress } from "./types";

class UnionFind {
  private parent = new Map<string, string>();
  add(value: string): void { if (!this.parent.has(value)) this.parent.set(value, value); }
  find(value: string): string {
    const parent = this.parent.get(value) ?? value;
    if (parent === value) return value;
    const root = this.find(parent);
    this.parent.set(value, root);
    return root;
  }
  union(left: string, right: string): void {
    this.add(left); this.add(right);
    const leftRoot = this.find(left);
    const rightRoot = this.find(right);
    if (leftRoot !== rightRoot) this.parent.set(rightRoot, leftRoot);
  }
  groups(): string[][] {
    const groups = new Map<string, string[]>();
    for (const value of this.parent.keys()) {
      const root = this.find(value);
      const group = groups.get(root) ?? [];
      group.push(value);
      groups.set(root, group);
    }
    return [...groups.values()].filter((group) => group.length > 1);
  }
}

function cameraFilename(name: string): boolean {
  return /^(IMG|DSC|DSCF|PXL|MVIMG|SAM|DCIM)[-_ ]?\d+/i.test(name);
}

function yearFromPath(path: string): number | undefined {
  const match = path.match(/(?:^|\/)(19|20)\d{2}(?:\/|$)/);
  return match ? Number(match[0].replaceAll("/", "")) : undefined;
}

function originalScore(photo: PhotoRecord): OriginalEvidence {
  let score = 0;
  const reasons: string[] = [];
  const pixels = photo.width * photo.height;
  if (pixels > 0) score += Math.log2(pixels);
  if (cameraFilename(photo.name)) { score += 3; reasons.push("camera filename"); }
  if (photo.takenAt) { score += 3; reasons.push("capture date retained"); }
  if (photo.camera) { score += 2; reasons.push("camera metadata retained"); }
  if (!photo.software) score += 1;
  else if (/photoshop|instagram|whatsapp|facebook|messenger|snapseed|export|lightroom/i.test(photo.software)) score -= 2;
  const pathYear = yearFromPath(photo.relativePath);
  const captureYear = photo.takenAt ? Number(photo.takenAt.slice(0, 4)) : undefined;
  if (pathYear && captureYear && pathYear === captureYear) { score += 2; reasons.push("date matches folder"); }
  score += Math.min(2, (photo.sharpness ?? 0) / 12);
  if (photo.cameraOriginal) {
    score += photo.cameraOriginal.score * 0.5;
    if (photo.cameraOriginal.verdict === "likely-camera-original") reasons.unshift("camera-original structure");
    if (photo.cameraOriginal.verdict === "possible-processed-copy") reasons.unshift("possible processed copy");
  }
  return {
    score,
    megapixels: pixels / 1_000_000,
    cameraFilename: cameraFilename(photo.name),
    captureDateRetained: Boolean(photo.takenAt),
    cameraMetadataRetained: Boolean(photo.camera),
    dateMatchesFolder: Boolean(pathYear && captureYear && pathYear === captureYear),
    editingSoftwareDetected: Boolean(photo.software && /photoshop|instagram|whatsapp|facebook|messenger|snapseed|export|lightroom/i.test(photo.software)),
    sharpness: photo.sharpness ?? 0,
    cameraOriginalScore: photo.cameraOriginal?.score,
    cameraOriginalVerdict: photo.cameraOriginal?.verdict,
    reasons,
  };
}

function recommendation(items: PhotoRecord[]): Pick<DuplicateGroup, "recommendedId" | "confidence" | "reasons" | "evidence"> {
  const evidence = Object.fromEntries(items.map((item) => [item.id, originalScore(item)]));
  const ranked = items.map((item) => ({ item, detail: evidence[item.id] })).sort((a, b) => b.detail.score - a.detail.score);
  const margin = ranked[0].detail.score - (ranked[1]?.detail.score ?? ranked[0].detail.score);
  return {
    recommendedId: ranked[0].item.id,
    confidence: margin >= 4 ? "high" : margin >= 2 ? "medium" : "review",
    reasons: ranked[0].detail.reasons.slice(0, 3),
    evidence,
  };
}

export async function findGroups(
  photos: PhotoRecord[],
  threshold: number,
  onProgress: (progress: ScanProgress) => void,
): Promise<DuplicateGroup[]> {
  const decodable = photos.filter((photo) => photo.visualHash && !photo.decodeError);
  const byId = new Map(photos.map((photo) => [photo.id, photo]));
  const exact = new Map<string, PhotoRecord[]>();
  for (const photo of photos) {
    if (!photo.exactHash) continue;
    const bucket = exact.get(photo.exactHash) ?? [];
    bucket.push(photo);
    exact.set(photo.exactHash, bucket);
  }

  const exactGroups: DuplicateGroup[] = [];
  const exactIds = new Set<string>();
  for (const items of exact.values()) {
    const roots = new Set(items.map((item) => item.rootId));
    if (items.length < 2 || roots.size < 2) continue;
    items.forEach((item) => exactIds.add(item.id));
    exactGroups.push({ id: `exact-${exactGroups.length + 1}`, type: "exact", items, ...recommendation(items) });
  }

  const index = new Map<string, PhotoRecord[]>();
  const pairs = new Map<string, [PhotoRecord, PhotoRecord]>();
  for (const photo of decodable) {
    const hash = photo.visualHash!;
    const seen = new Set<string>();
    for (let chunk = 0; chunk < 16; chunk += 1) {
      const key = `${chunk}:${hash.slice(chunk * 4, chunk * 4 + 4)}`;
      for (const candidate of index.get(key) ?? []) {
        if (candidate.rootId === photo.rootId || seen.has(candidate.id)) continue;
        seen.add(candidate.id);
        const pairKey = [candidate.id, photo.id].sort().join("||");
        pairs.set(pairKey, [candidate, photo]);
      }
      const bucket = index.get(key) ?? [];
      bucket.push(photo);
      index.set(key, bucket);
    }
  }

  const candidates = [...pairs.values()].filter(([left, right]) =>
    hammingDistance(left.visualHash!, right.visualHash!) <= threshold,
  );
  const union = new UnionFind();
  const metrics = new Map<string, Awaited<ReturnType<typeof compareImages>>>();
  for (let index = 0; index < candidates.length; index += 1) {
    const [left, right] = candidates[index];
    onProgress({ phase: "Verifying matches", current: index + 1, total: candidates.length, detail: `${left.name} ↔ ${right.name}` });
    const pairMetrics = await compareImages(left, right);
    const isMatch = pairMetrics.ssim >= 0.94 || pairMetrics.psnr >= 34;
    if (!isMatch) continue;
    union.union(left.id, right.id);
    metrics.set([left.id, right.id].sort().join("||"), pairMetrics);
  }

  const visualGroups = union.groups().map((ids, index) => {
    const items = ids.map((id) => byId.get(id)!).filter(Boolean);
    const pair = metrics.get([items[0].id, items[1]?.id ?? ""].sort().join("||"));
    return {
      id: `visual-${index + 1}`,
      type: "visual" as const,
      items,
      metrics: pair,
      ...recommendation(items),
    };
  }).filter((group) => !group.items.every((item) => exactIds.has(item.id)));

  const groups = [...exactGroups, ...visualGroups].sort((left, right) => {
    if (left.type !== right.type) return left.type === "exact" ? -1 : 1;
    return right.items.length - left.items.length;
  });
  await enrichGroups(groups, onProgress);
  return groups;
}

export async function enrichGroups(
  groups: DuplicateGroup[],
  onProgress: (progress: ScanProgress) => void = () => undefined,
): Promise<void> {
  const groupedPhotos = [...new Map(groups.flatMap((group) => group.items).map((photo) => [photo.id, photo])).values()];
  for (let index = 0; index < groupedPhotos.length; index += 1) {
    const photo = groupedPhotos[index];
    if (!photo.exactHash) photo.exactHash = await exactHash(photo.file);
    if (!photo.pixelHash && !photo.decodeError) photo.pixelHash = await decodedPixelHash(photo.file);
    if (!photo.metadata) {
      try { photo.metadata = await readFullMetadata(photo.file); }
      catch { photo.metadata = []; }
    }
    if (!photo.container) photo.container = await inspectContainer(photo.file, photo.metadata);
    onProgress({ phase: "Building forensic evidence", current: index + 1, total: groupedPhotos.length, detail: photo.relativePath });
  }
  for (const group of groups) {
    for (const photo of group.items) photo.cameraOriginal = assessCameraOriginal(photo, group.items);
    Object.assign(group, recommendation(group.items));
  }
}
